import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CompanyUpdateInput } from "../../../api/Companies";
import { useCompanyUpdate, useGetCompanyEx } from "../../../api/CompaniesApi";

import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { Transition } from "history";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../../shared-components/ConfirmationDialog";
import { useBlocker } from "../../../utils/blocker";
import InputField from "../../../shared-components/InputField";
import {
  useDeleteApiKey,
  useGenerateNewApiKey,
  useGetApiKey,
} from "../../../api/KeysApi";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useApplicationToast } from "../../../webcontent/ApplicationToastProvider";
import CompanyCallbackHeaders, {
  CallbackHeadersDefinitions,
} from "../../companyEdit/CompanyCallbackHeaders";
import CompanyUpdateToggle from "../../companyEdit/CompanyUpdateToggle";
import Button, { ButtonTypes } from "../../../shared-components/Button";

const defaultLocals: CompanyUpdateInput = {
  companyUUID: "",
  updates: new Map(),
};
const properties: string[] = [
  "callback_config",
  "callback_on_amendment_complete",
];

const ApiSettings = () => {
  const { companyId, subid } = useParams();
  const activeId: string = !!subid ? subid : companyId ?? "";
  const { companies } = usePermissionValidator();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [locals, setLocals] = useState<CompanyUpdateInput>(defaultLocals);
  const [headers, setHeaders] = useState<any>(undefined);
  const [dirty, setDirty] = useState<boolean>(false);
  const [transition, setTransition] = useState<Transition | undefined>(
    undefined
  );

  const navigate = useNavigate();
  if (!companies.canViewApiKeys) {
    navigate("/unauthorized", { replace: true });
  }

  const {
    isLoading: loadingCompanyEx,
    data: companyEx,
    mutate: loadCompanyEx,
  } = useGetCompanyEx();
  const { isLoading: loadingSave, mutateAsync: companySave } =
    useCompanyUpdate();
  const {
    isLoading: loadingApiKey,
    data: apiKey,
    mutate: loadApiKey,
  } = useGetApiKey();
  const { isLoading: generatingApiKey, mutate: generateApiKey } =
    useGenerateNewApiKey();
  const { showLargeToast } = useApplicationToast();

  const { isLoading: deletingApiKey, mutateAsync: deleteApiKey } =
    useDeleteApiKey();

  useEffect(() => {
    if (locals.companyUUID !== defaultLocals.companyUUID) {
      return;
    }
    loadApiKey(activeId);
    loadCompanyEx(activeId);
    let newLocals = { ...locals };
    newLocals.companyUUID = activeId;
    setLocals(newLocals);
  }, [activeId, loadApiKey, loadCompanyEx, setLocals, locals]);

  useEffect(() => {
    setReadOnly(!companies.canCreateApiKeys);
  }, [setReadOnly, companies]);

  useEffect(() => {
    if (!companyEx) {
      setLocals(defaultLocals);
      return;
    }
    const values = new Map<string, any>();
    properties.forEach((property) => {
      const value = companyEx[property as keyof typeof companyEx];
      values.set(
        property,
        property === "callback_on_amendment_complete" ? value ?? false : value
      );
    });
    const newLocals: CompanyUpdateInput = {
      companyUUID: companyEx.external_id,
      updates: values,
    };
    setLocals(newLocals);
    setHeaders(newLocals.updates.get("callback_config")?.headers);
  }, [companyEx]);

  function updateState(key: string, value: any) {
    let newLocals: CompanyUpdateInput = { ...locals };
    if (key.includes(".")) {
      const chunks = key.split(".");
      let fullValue = newLocals.updates.get(chunks[0]);
      fullValue[chunks[1]] = value;
      newLocals.updates.set(chunks[0], fullValue);
    } else {
      newLocals.updates.set(key, value);
    }
    setLocals(newLocals);
    setDirty(true);
  }

  async function clearApiKey() {
    if (!companies.canCreateApiKeys) {
      return;
    }
    await deleteApiKey(activeId);
    loadApiKey(activeId);
    showLargeToast?.({ title: "Success", message: "Api Key Deleted" });
  }

  async function saveChanges() {
    if (!readOnly) {
      companySave(locals);
      setDirty(false);
    }
  }

  useEffect(() => {
    if (transition !== undefined) {
      transition.retry();
    }
  }, [transition]);

  const { hide, modal, show } = useConfirmationDialog();
  function promptDirtyExit(tx: Transition) {
    if (!dirty) {
      setTransition(tx);
      return true;
    }
    show(
      "Leave without saving changes?",
      "You have unsaved changes. If you leave now, your edits will be lost.",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          setDirty(false);
          setTransition(tx);
          return true;
        } else {
          hide();
        }
      }
    );
  }

  useBlocker((tx: Transition) => {
    promptDirtyExit(tx);
  });

  return (
    <div className="p-7 bg-gray-50 max-w-[800px]">
      {modal}
      {loadingCompanyEx || !companyEx || !locals.updates.has(properties[0]) ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div className="w-full p-3">
            {deletingApiKey || loadingApiKey ? (
              <LoadingSpinner />
            ) : (
              <div className="w-1/2">
                {apiKey && (
                  <InputField
                    readOnly={true}
                    name="api_key"
                    hideText={true}
                    value={apiKey?.key}
                    label="API Key"
                    onClear={
                      companies.canCreateApiKeys ? clearApiKey : undefined
                    }
                    isCopyable={true}
                    labelClassName="!text-lg"
                    classes=""
                  />
                )}
                {companies.canCreateApiKeys && (
                  <button
                    className="flex flex-row mt-4 pb-4 gap-3"
                    onClick={() => {
                      generateApiKey(activeId, {
                        onSuccess: () => {
                          loadApiKey(activeId);
                          showLargeToast?.({
                            title: "Success",
                            message: "New Key Generated",
                          });
                        },
                      });
                    }}
                  >
                    <ArrowPathIcon
                      className="text-ourGreen"
                      width={20}
                    ></ArrowPathIcon>
                    {generatingApiKey
                      ? "Generating..."
                      : "Generate New API key"}
                  </button>
                )}
              </div>
            )}
            <div className="py-6">
              <InputField
                readOnly={readOnly}
                handleChange={(e) => {
                  updateState("callback_config.url", e.target.value);
                }}
                name={"api_url"}
                defaultValue={locals.updates.get("callback_config")?.url ?? ""}
                label={"Callback URL"}
                labelClassName="!text-lg mb-5"
              />
            </div>
            <div className="py-6">
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="callback_on_amendment_complete"
                label="Enable Callback For Amended Reports"
                labelClassName="!text-lg mr-4"
                classNames="w-fit"
              />
            </div>
            <div className="py-6">
              <InputField
                readOnly={readOnly}
                handleChange={(e) => {
                  updateState("callback_config.qa_url", e.target.value);
                }}
                name={"qa_url"}
                defaultValue={
                  locals.updates.get("callback_config")?.qa_url ?? ""
                }
                label={"Investigator Callback URL"}
                labelClassName="!text-lg mb-5"
              />
            </div>
            <div>
              <CompanyCallbackHeaders
                prefill={headers}
                onChange={(e: CallbackHeadersDefinitions[]) => {
                  updateState(
                    "callback_config.headers",
                    Object.fromEntries(e.map((v) => [v.key, v.value]))
                  );
                }}
                labelClassName="!text-lg mb-5"
                readOnly={readOnly}
              />
              <Button
                text="Save Changes"
                type={loadingSave ? ButtonTypes.saving : ButtonTypes.spacing}
                onClick={saveChanges}
                className="ml-4"
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiSettings;
